import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, useContext, onMounted, computed, nextTick } from '@nuxtjs/composition-api'
import { PropType } from 'vue'
import { IBanksItem, TBanksModal } from '@entities/banks/model/types'
import { BanksFavorite } from '@entities/banks'
import { useThemeModel } from '@entities/theme'
import { useRestaurantModel } from '@entities/restaurant'
import { usePaymentModals } from '@entities/paymentModals/usePaymentModals'
import PaymentModals from '@/components/Payment/PaymentModals.vue'
import { useOrderModel } from '@entities/order'


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentBanks',
  props: {
  banksModal: {
    type: String as PropType<TBanksModal>,
    required: true,
    default: 'priority'
  },
  sbpModal: {
    type: Boolean as PropType<boolean>,
    required: true,
    default: false
  },
  totalSum: {
    type: String as PropType<string>,
    required: false,
    default: '0'
  },
  tips: {
    type: String as PropType<string>,
    required: false,
    default: '0'
  }
},
  emits: ['toggleBanksModal', 'pay'],
  setup(__props, { emit: emits }) {

const props = __props

const { $cookies } = useContext()
const themeModel = useThemeModel()
const restaurantModel = useRestaurantModel()
const orderModel = useOrderModel()
const {
  modalState,
  openConfirmModal,
  handleCheckPrinted,
  proceedToPayment,
  handlePaymentCancel,
  handleCheckPrintClose,
  printCheck
} = usePaymentModals()



// Data
const check = ref<boolean>(false)
const error = ref<boolean>(false)
const email = ref<string>('')
const showEmailError = ref<boolean>(false)
const emailErrorText = ref<string>('')
const emailInputRef = ref<any>(null)
const pendingPaymentData = ref<{ schemaPrefix: string; email: string } | null>(null)

const invalidEmailsSet = new Set([
  '1@yandex.ru',
  '12@yandex.ru',
  '123@yandex.ru',
  '1234@yandex.ru',
  '12345@yandex.ru',
  '123456@yandex.ru',
  '1234567@yandex.ru',
  '1234567@bk.ru',
  '12345@bk.ru',
  '1@bk.ru',
  '1@gmail.com',
  '2@gmail.com',
  '3@gmail.com',
  '11@gmail.com',
  '12@gmail.com',
  '123@gmail.com',
  '1234@gmail.com',
  '12345@gmail.com',
  '123456@gmail.com',
  '1@mail.ru',
  '123@mail.ru',
  '1234@mail.ru',
  '12345@mail.ru'
].map(email => email.trim().toLowerCase()))
const invalidEmailWarningText = 'указанный адрес электронной почты не существует'

const isBlockedEmail = (value: string): boolean => invalidEmailsSet.has(value.trim().toLowerCase())

// Computed
const sumTotalSum = computed(() => (parseFloat(props.totalSum) + parseFloat(props.tips)).toFixed(2))
const saveEmailToCookies = (email: string): boolean => {
  if (!isValidEmailFormat(email) || isBlockedEmail(email)) {
    $cookies.remove('email')
    return false
  }
  $cookies.set('email', email, {
    maxAge: 60 * 60 * 24 * 30
  })
  return true
}

const isValidEmailFormat = (email: string): boolean => {
  const trimmed = email.trim()
  if (!trimmed || /^[.,:-]/.test(trimmed)) {
    return false
  }
  const parts = trimmed.split('@')
  if (parts.length !== 2) {
    return false
  }
  const [localPart, domainPart] = parts
  if (
    !localPart ||
    !domainPart ||
    localPart.length > 64 ||
    /^\./.test(localPart) ||
    /\.$/.test(localPart) ||
    /\.@/.test(trimmed) ||
    localPart.includes('..') ||
    /[[\]<>(),;:\s@]/.test(localPart)
  ) {
    return false
  }
  // Только латиница/цифры и спецсимволы: ! # $ % & ' * + - / = ? ^ _ { | } ~ .
  // Локальная часть не может начинаться/заканчиваться точкой, пробелов и < > ( ) [ ] , ; : нет
  const emailRegex = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~](?:[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]*[a-zA-Z0-9!#$%&'*+/=?^_`{|}~])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
  const hasValidDomain = trimmed.includes('.') && trimmed.split('.').pop()!.length > 1
  return emailRegex.test(trimmed) && hasValidDomain
}

const isAllowedValidEmail = (value: string): boolean => isValidEmailFormat(value) && !isBlockedEmail(value)



// Watch
watch(email, (newEmail) => {
  showEmailError.value = false
  emailErrorText.value = ''
  const trimmedEmail = newEmail.trim()

  if (trimmedEmail === '') {
    error.value = false
    $cookies.remove('email')
    $cookies.set('emailError', false, {
      maxAge: 60 * 60 * 24 * 30
    })
    showEmailError.value = false
    return
  }

  if (isBlockedEmail(trimmedEmail)) {
    error.value = true
    emailErrorText.value = invalidEmailWarningText
    $cookies.remove('email')
    $cookies.set('emailError', true, {
      maxAge: 60 * 60 * 24 * 30
    })
    return
  }

  const isValid = isValidEmailFormat(trimmedEmail)

  if (isValid) {
    saveEmailToCookies(trimmedEmail)
    error.value = false
  } else {
    $cookies.remove('email')
    error.value = true
  }

  $cookies.set('emailError', error.value, {
    maxAge: 60 * 60 * 24 * 30
  })
  showEmailError.value = false
})

// Methods
const toggleBanksModal = (val: TBanksModal) => {
  emits('toggleBanksModal', val)
}

const getError = (val: boolean) => {
  const trimmedEmail = email.value.trim()

  if (trimmedEmail && isBlockedEmail(trimmedEmail)) {
    error.value = true
    emailErrorText.value = invalidEmailWarningText
    $cookies.remove('email')
    $cookies.set('emailError', true, {
      maxAge: 60 * 60 * 24 * 30
    })
    return
  }

  error.value = val
  if (val) {
    $cookies.remove('email')
  } else if (email.value.trim() !== '') {
    emailErrorText.value = ''
    if (isAllowedValidEmail(email.value)) {
      saveEmailToCookies(email.value)
    }
  }

  $cookies.set('emailError', val, {
    maxAge: 60 * 60 * 24 * 30
  })
  if (!val) {
    showEmailError.value = false
  }
}

const toggleCheck = () => {
  if (restaurantModel.isEmailRequired) {
    return
  }

  const oldCheckValue = check.value
  check.value = !check.value
  if (oldCheckValue && !check.value) {
    error.value = false
    showEmailError.value = false

    // Если email невалидный - обнуляем его и сохраняем пустую строку в куки
    if (email.value && !isAllowedValidEmail(email.value)) {
      email.value = ''
      $cookies.set('email', '', {
        maxAge: 60 * 60 * 24 * 30
      })
      $cookies.set('emailError', false, {
        maxAge: 60 * 60 * 24 * 30
      })
    }
  }

  // Сохраняем email только при установке чекбокса, если он валиден
  if (!oldCheckValue && check.value && email.value && isAllowedValidEmail(email.value)) {
    saveEmailToCookies(email.value)
  }
}

const focusEmailInput = async () => {
  await nextTick()
  if (emailInputRef.value && emailInputRef.value.focus) {
    emailInputRef.value.focus()
  } else if (emailInputRef.value && emailInputRef.value.$el) {
    const inputElement = emailInputRef.value.$el.querySelector('input')
    if (inputElement) {
      inputElement.focus()
    }
  }
}

const pay = async (val: IBanksItem) => {
  let hasInputError = false
  if (emailInputRef.value) {
    const inputElement = emailInputRef.value.$el || emailInputRef.value
    hasInputError = inputElement.classList?.contains('err') ||
      inputElement.classList?.contains('error') ||
      !!inputElement.querySelector?.('.err') ||
      !!inputElement.querySelector?.('.error')
  }

  if (hasInputError) {
    await focusEmailInput()
    return
  }
  const emailTrimmed = email.value.trim()
  if (restaurantModel.isEmailRequired) {
    const invalidRequiredEmail = !emailTrimmed || !isAllowedValidEmail(emailTrimmed)
    if (invalidRequiredEmail) {
      // Показываем общий текст, если пусто; если формат неверный — подсвечиваем поле
      if (emailTrimmed === '') {
        showEmailError.value = true
      } else {
        error.value = true
        emailErrorText.value = 'E-mail содержит ошибку'
        showEmailError.value = false
      }
      await focusEmailInput()
      return
    }
  }
  if (!restaurantModel.isEmailRequired && check.value && !emailTrimmed) {
    showEmailError.value = true
    await focusEmailInput()
    return
  }
  const shouldSendEmail = restaurantModel.isEmailRequired || check.value

  if (shouldSendEmail && emailTrimmed && !isAllowedValidEmail(emailTrimmed)) {
    error.value = true
    emailErrorText.value = 'E-mail содержит ошибку'
    showEmailError.value = false
    await focusEmailInput()
    return
  }

  const emailToSend = shouldSendEmail ? emailTrimmed : ''

  if (shouldSendEmail && emailTrimmed && isAllowedValidEmail(emailTrimmed)) {
    saveEmailToCookies(emailTrimmed)
  }

  // Сохранение данных для оплаты
  const paymentData = {
    schemaPrefix: val?.schema_prefix || '',
    email: emailToSend
  }

  // Если требуется подтверждение - показываем модалку
  const needsConfirm = restaurantModel.isPreliminaryCheckOfIntent &&
    !(orderModel.orderData?.orders?.[0]?.preliminaryCheckOfIntent)

  if (needsConfirm) {
    pendingPaymentData.value = paymentData
    openConfirmModal()
  } else {
    // Иначе сразу продолжаем оплату
    emits('pay', paymentData)
  }
}

// Функция для завершения оплаты
const completePayment = () => {
  if (pendingPaymentData.value) {
    emits('pay', pendingPaymentData.value)
    pendingPaymentData.value = null
  } else {
    console.warn('No pending payment data found')
  }
}

const handleModalCheckPrintClose = () => {
  handleCheckPrintClose(() => {
    completePayment()
  })
}

const handleCheckPrintedWithPayment = () => {
  handleCheckPrinted()
  completePayment()
}

const proceedWithPayment = () => {
  if (!orderModel.orderData) {
    return
  }

  proceedToPayment(() =>
    printCheck({
      orderData: orderModel.orderData,
      totalAmount: parseFloat(sumTotalSum.value),
      restaurantCode: restaurantModel.code
    })
  )
}

// Mounted
onMounted(() => {
  const emailCookie = $cookies.get('email')
  const errorCookie = $cookies.get('emailError')
  if (emailCookie) {
    const isValid = isAllowedValidEmail(emailCookie)

    if (isValid) {
      email.value = emailCookie
      error.value = errorCookie === 'true' || errorCookie === true
    } else {
      $cookies.remove('email')
      $cookies.remove('emailError')
      error.value = false
    }
  }
  if (restaurantModel.isEmailRequired) {
    check.value = true
  }
})

return { __sfc: true,$cookies, themeModel, restaurantModel, orderModel, modalState, openConfirmModal, handleCheckPrinted, proceedToPayment, handlePaymentCancel, handleCheckPrintClose, printCheck, props, check, error, email, showEmailError, emailErrorText, emailInputRef, pendingPaymentData, invalidEmailsSet, invalidEmailWarningText, isBlockedEmail, sumTotalSum, saveEmailToCookies, isValidEmailFormat, isAllowedValidEmail, emits, toggleBanksModal, getError, toggleCheck, focusEmailInput, pay, completePayment, handleModalCheckPrintClose, handleCheckPrintedWithPayment, proceedWithPayment, BanksFavorite, PaymentModals }
}

})